<template>
  <teleport to="body">
    <modal-base :show="modalActive" :title="modalTitle" :align-footer="'center'" @on-close="closeModal">
      <template #header>
        <h5 id="modalRoomingLabel" class="modal-title ms-2 ms-md-0">
          {{ t('rooming.organizarDistribucion') }}
          <InfoIcon size="12px" :message-info="t('rooming.tooltipMessage')" />
        </h5>
      </template>
      <template #body>
        <div class="d-flex flex-column flex-md-row text-primary">
          <span class="title me-2 d-flex align-items-center ms-2 ms-md-0">{{ roomingData.viaje.toUpperCase() }}</span>

          <span class="vr d-none d-md-block ms-2 vertical-rule"></span>

          <div class="mt-2 mt-md-0 d-flex align-items-center flex-md-row">
            <CalendarIcon color="var(--bs-primary)" class="mx-2" />
            <span class="subtitle">
              {{ formatDate(roomingData.fechaInicio) }}
              <template v-if="roomingData.fechaInicio !== roomingData.fechaFin">
                - {{ formatDate(roomingData.fechaFin) }}
              </template>
            </span>
          </div>
        </div>
        <div class="contenido my-3 gap-2">
          <div class="columna-alojamientos">
            <div class="card-alojamiento-container">
              <CardAlojamientoRooming
                :rooming-data="roomingData"
                :usuarios="usuarios"
                :alojamiento-editando="alojamientoEditando"
                :habitacion-editando="habitacionEditando"
                :expanded-habitacion-id="expandedHabitacionId"
                @actualizar="actualizarHabitaciones"
                @is-editing="actualizarSeleccionables"
                @eliminar-integrante="eliminarIntegrante"
                @toggle-expand="toggleExpand" />
            </div>
          </div>
          <div class="columna-tabla">
            <TablaRooming
              :usuarios="usuariosTabla"
              :alojamiento-seleccionado="roomingData.nombre"
              :is-editing="editMode"
              @seleccionar-usuario="agregarIntegrante" />
          </div>
        </div>
      </template>
      <template #footer>
        <div v-if="estadoRooming === ESTADOS_ROOMING.EN_EDICION" class="d-flex gap-3 mb-1">
          <button class="text-primary btn-link" @click="closeModal()">{{ t('botones.cancelar') }}</button>
          <button class="btn btn-primary btn-guardar" @click="modalGuardarRoomingActive = true">
            {{ t('botones.guardar') }}
          </button>
        </div>
        <div v-else class="mb-1">
          <button class="btn btn-primary" @click="closeModal()">{{ t('botones.aceptar') }}</button>
        </div>
      </template>
    </modal-base>
  </teleport>

  <ModalGuardarRooming
    :rooming-data="roomingData"
    :modal-active="modalGuardarRoomingActive"
    @close-modal="closeGuardarRoomingModal()"
    @close-all-modals="closeAllModals()" />
</template>

<script setup>
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import CardAlojamientoRooming from '@/components/card/rooming/CardAlojamientoRooming.vue';
  import TablaRooming from '@/components/tablas/TablaRooming.vue';
  import InfoIcon from '@/components/Icons/InfoIcon.vue';
  import ModalBase from '@/components/modales/ModalBase.vue';
  import ModalGuardarRooming from '@/components/modales/Rooming/ModalGuardarRooming.vue';
  import { controlErrorAlojamientos } from '@/services/utils/ErrorControl';
  import { formatDate } from '@/helpers/dateUtiles';
  import { ESTADOS_ROOMING } from '@/constants/roomingConstants';
  import CalendarIcon from '@/components/Icons/CalendarIcon.vue';

  const { t } = useI18n();

  const props = defineProps({
    roomingData: {
      type: Object,
      default: () => ({ habitaciones: [] }),
    },
    estadoRooming: {
      type: String,
      required: true,
    },
    modalActive: {
      type: Boolean,
      required: true,
    },
  });

  const emits = defineEmits(['actualizar', 'is-editing', 'close-modal']);

  const alojamientoSeleccionadoId = ref(null);
  const alojamientoEditandoId = ref(null);
  const habitacionEditandoId = ref(null);
  const expandedHabitacionId = ref(null);
  const editMode = ref(false);
  const usuariosTabla = ref([]);
  const modalGuardarRoomingActive = ref(false);

  const modalTitle = t('rooming.organizarDistribucion');

  function actualizarSeleccionables(isEditing, idAlojamiento, idHabitacion) {
    alojamientoEditandoId.value = idAlojamiento;
    habitacionEditandoId.value = idHabitacion;
    alojamientoSeleccionadoId.value = idAlojamiento;
    editMode.value = isEditing;
  }

  function actualizarHabitaciones(nuevasHabitaciones) {
    controlErrorAlojamientos('actualizar', nuevasHabitaciones);
  }

  function agregarIntegrante(usuario) {
    const habitacion = props.roomingData.habitaciones.find(h => h.id === habitacionEditandoId.value);
    if (habitacion && habitacion.integrantes.length < habitacion.numPlazas) {
      habitacion.integrantes.push(usuario);
      usuario.asignado = true;
      orderUsuariosTabla();
    }
  }

  function eliminarIntegrante(idHabitacion, integrante) {
    const habitacion = props.roomingData.habitaciones.find(h => h.id === idHabitacion);
    if (habitacion) {
      habitacion.integrantes = habitacion.integrantes.filter(i => i !== integrante);
      const usuario = usuariosTabla.value.find(u => u.id === integrante.id);
      if (usuario) {
        usuario.asignado = false;
        orderUsuariosTabla();
      }
    }
  }

  function orderUsuariosTabla() {
    usuariosTabla.value.sort((a, b) => a.id - b.id);
  }

  function toggleExpand(habitacionId) {
    expandedHabitacionId.value = habitacionId;
  }

  function closeModal() {
    emits('close-modal');
  }

  function closeGuardarRoomingModal() {
    modalGuardarRoomingActive.value = false;
  }

  function closeAllModals() {
    closeGuardarRoomingModal();
    closeModal();
  }

  function updateUsuariosTabla() {
    const usuariosSinAsignacion = props.roomingData.usuariosSinAsignacion || [];
    const habitaciones = props.roomingData.habitaciones || [];

    const habitacionesSinIntegrantes = habitaciones.map(habitacion => habitacion.integrantes.length === 0);
    const todasHabitacionesSinIntegrantes =
      habitacionesSinIntegrantes.filter(sinIntegrantes => sinIntegrantes).length === habitaciones.length;

    if (usuariosSinAsignacion.length === 0 && todasHabitacionesSinIntegrantes) {
      usuariosTabla.value = [];
      return;
    }

    const nuevosUsuarios = [
      ...usuariosSinAsignacion.map(usuario => ({ ...usuario, asignado: false })),
      ...habitaciones.flatMap(habitacion =>
        habitacion.integrantes.map(integrante => ({ ...integrante, asignado: true }))
      ),
    ];

    usuariosTabla.value = usuariosTabla.value.map(usuarioActual => {
      const usuarioNuevo = nuevosUsuarios.find(nuevo => nuevo.id === usuarioActual.id);
      return usuarioNuevo ? usuarioNuevo : usuarioActual;
    });

    nuevosUsuarios.forEach(usuarioNew => {
      if (!usuariosTabla.value.find(usuarioActual => usuarioActual.id === usuarioNew.id)) {
        usuariosTabla.value.push(usuarioNew);
      }
    });
  }

  watch(
    () => props.modalActive,
    () => {
      updateUsuariosTabla();
    },
    { immediate: true, deep: true }
  );
</script>

<style scoped lang="css">
  .modal-title {
    color: var(--bs-primary);
  }

  .modal-content {
    max-width: 1000px;
    margin: auto;
  }

  .contenido {
    display: flex;
    width: 100%;
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .columna-alojamientos {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-right: 20px;
    max-width: 45%;
  }

  .card-alojamiento-container {
    width: 100%;
  }

  .columna-tabla {
    flex: 1;
    max-width: 50%;
    min-width: 0;
  }

  .contenido::-webkit-scrollbar {
    width: 8px;
  }

  .contenido::-webkit-scrollbar-thumb {
    border-radius: var(--bs-border-radius-sm);
    background: rgba(var(--bs-primary-rgb), 0.2);
  }

  :deep(.rooming-modal) {
    max-width: 90vw !important;
    width: 100% !important;
  }

  :deep(.rooming-modal-content) {
    height: 85vh !important;
    background: white !important;
  }

  .modal-footer {
    display: flex;
    justify-content: center;
    gap: 10px;
  }

  .btn-link {
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.12px;
    text-decoration: underline;
    color: var(--bs-primary);
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
  }

  @media (max-width: 1024px) {
    :deep(.rooming-modal) {
      max-width: 95vw !important;
      width: 100% !important;
    }

    :deep(.rooming-modal-content) {
      height: 95vh !important;
    }
  }
  @media (max-width: 768px) {
    .columna-alojamientos,
    .columna-tabla {
      flex: 1;
      max-width: 55%;
    }

    .columna-alojamientos {
      margin-right: 2px;
    }

    :deep(.modal-body) {
      position: relative;
      flex: 1 1 auto;
      padding: 3px;
    }

    :deep(.modal-container) {
      padding-left: 8px;
      padding-right: 8px;
    }
  }

  .title {
    font-weight: 700;
  }
  .subtitle {
    font-weight: 500;
    font-size: 14px;
  }

  .vertical-rule {
    opacity: 1 !important;
  }

  .btn-guardar {
    box-shadow: 0px 1px 2px 0px rgba(var(--bs-primary-rgb), 0.35) !important;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 20px;
  }
</style>
